import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  EmailPipelinePageLogicContracts,
  EmailPipelineRecord,
  EmailPipelineRecordDetailsResponse,
  EmailPipelineStatusResponse,
  EmailPipelineStoreOption,
} from '../../../types/features/emailPipeline';
import storeService from '../../../services/storeService';
import emailPipelineService from '../services/emailPipelineService';
import { getStoreLabel } from '../mappers/emailPipelineFormatters';
import { PAGE_SIZE, getTotalRecordsFromResponse, parseInitialFilters } from '../mappers/emailPipelineNormalizers';
import showToast from '../../../utils/toastUtils';
import { formatDateForApi } from '../../../utils/formatters/dateFormatters';
import { getErrorMessage } from '../../../utils/normalizers/errorNormalizers';
import { isProcessedStatus } from '../../../utils/normalizers/statusNormalizers';
import { isSuperAdmin as getIsSuperAdmin } from '../../../utils/auth/currentUser';
import {
  EMAIL_PIPELINE_DATE_FROM_PARAM,
  EMAIL_PIPELINE_DATE_TO_PARAM,
  EMAIL_PIPELINE_PAGE_PARAM,
  EMAIL_PIPELINE_PAGE_SIZE_PARAM,
  EMAIL_PIPELINE_STATUS_PARAM,
  EMAIL_PIPELINE_STORE_PARAM,
} from '../utils/emailPipelineQueryParams';

type UseEmailPipelinePageLogicOptions = {
  initialStoreId?: string;
  lockStoreSelection?: boolean;
};

export const useEmailPipelinePageLogic = ({
  initialStoreId,
  lockStoreSelection = false,
}: UseEmailPipelinePageLogicOptions = {}): EmailPipelinePageLogicContracts => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(() => parseInitialFilters(searchParams), [searchParams]);
  const isSuperAdmin = getIsSuperAdmin();

  const [page, setPage] = useState<number>(initialFilters.page);
  const [statusFilter, setStatusFilter] = useState<string>(initialFilters.status);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(initialStoreId || initialFilters.storeId);
  const [dateRangeFilter, setDateRangeFilter] = useState<[Date | null, Date | null]>(initialFilters.dateRange);
  const [search, setSearch] = useState<string>('');
  const [storeSearchInput, setStoreSearchInput] = useState<string>('');

  const [statusData, setStatusData] = useState<EmailPipelineStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [statusError, setStatusError] = useState<string>('');

  const [records, setRecords] = useState<EmailPipelineRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsLoading, setRecordsLoading] = useState<boolean>(false);
  const [recordsError, setRecordsError] = useState<string>('');

  const [stores, setStores] = useState<EmailPipelineStoreOption[]>([]);
  const [storesLoading, setStoresLoading] = useState<boolean>(false);
  const [storesError, setStoresError] = useState<string>('');

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [detailsData, setDetailsData] = useState<EmailPipelineRecordDetailsResponse | null>(null);
  const [detailsError, setDetailsError] = useState<string>('');
  const [detailsActionLoading, setDetailsActionLoading] = useState<boolean>(false);
  const [detailsActionError, setDetailsActionError] = useState<string>('');
  const [detailsActionSuccess, setDetailsActionSuccess] = useState<string>('');
  const [bulkActionLoading, setBulkActionLoading] = useState<boolean>(false);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedStore = useMemo(() => stores.find((store) => store.id === selectedStoreId) || null, [stores, selectedStoreId]);
  const detailsRecord = detailsData?.result?.records?.[0];

  const syncUrlParams = useCallback(() => {
    const params = new URLSearchParams(searchParams);

    params.set(EMAIL_PIPELINE_PAGE_PARAM, String(page));
    params.set(EMAIL_PIPELINE_PAGE_SIZE_PARAM, String(PAGE_SIZE));

    if (statusFilter) {
      params.set(EMAIL_PIPELINE_STATUS_PARAM, statusFilter);
    } else {
      params.delete(EMAIL_PIPELINE_STATUS_PARAM);
    }

    if (selectedStoreId) {
      params.set(EMAIL_PIPELINE_STORE_PARAM, selectedStoreId);
    } else {
      params.delete(EMAIL_PIPELINE_STORE_PARAM);
    }

    if (dateRangeFilter[0]) {
      params.set(EMAIL_PIPELINE_DATE_FROM_PARAM, formatDateForApi(dateRangeFilter[0]));
    } else {
      params.delete(EMAIL_PIPELINE_DATE_FROM_PARAM);
    }

    if (dateRangeFilter[1]) {
      params.set(EMAIL_PIPELINE_DATE_TO_PARAM, formatDateForApi(dateRangeFilter[1]));
    } else {
      params.delete(EMAIL_PIPELINE_DATE_TO_PARAM);
    }

    setSearchParams(params, { replace: true });
  }, [dateRangeFilter, page, searchParams, selectedStoreId, setSearchParams, statusFilter]);

  useEffect(() => {
    if (!initialStoreId || selectedStoreId === initialStoreId) {
      return;
    }

    setSelectedStoreId(initialStoreId);
    setPage(1);
  }, [initialStoreId, selectedStoreId]);

  const fetchStores = useCallback(async () => {
    setStoresLoading(true);
    setStoresError('');

    try {
      const response = await storeService.getStores({ get_all: true });
      setStores(response.stores || []);
    } catch (error: unknown) {
      setStoresError(getErrorMessage(error, 'Failed to load stores'));
      setStores([]);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError('');

    try {
      const response = await emailPipelineService.getStatus();
      setStatusData(response);
    } catch (error: unknown) {
      setStatusError(getErrorMessage(error, 'Failed to load pipeline status'));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setRecordsLoading(true);
    setRecordsError('');

    try {
      const response = await emailPipelineService.getRecords({
        status: statusFilter || undefined,
        store: selectedStoreId || undefined,
        subject_contains: search.trim() || undefined,
        date_from: dateRangeFilter[0] ? formatDateForApi(dateRangeFilter[0]) : undefined,
        date_to: dateRangeFilter[1] ? formatDateForApi(dateRangeFilter[1]) : undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setRecords(response?.result?.records || []);
      setTotalRecords(getTotalRecordsFromResponse(response));
    } catch (error: unknown) {
      setRecordsError(getErrorMessage(error, 'Failed to load records'));
      setRecords([]);
      setTotalRecords(0);
    } finally {
      setRecordsLoading(false);
    }
  }, [dateRangeFilter, page, search, selectedStoreId, statusFilter]);

  useEffect(() => {
    syncUrlParams();
  }, [syncUrlParams]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchStatus();
    if (!lockStoreSelection) {
      fetchStores();
    }
  }, [fetchStatus, fetchStores, isSuperAdmin, lockStoreSelection]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchRecords();
  }, [fetchRecords, isSuperAdmin]);

  useEffect(() => {
    setStoreSearchInput(selectedStore ? getStoreLabel(selectedStore) : '');
  }, [selectedStore]);

  useEffect(() => {
    setSelectedIds([]);
  }, [records]);

  const handleStoreChange = useCallback((storeId: string) => {
    setSelectedStoreId(storeId);
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((status: string) => {
    setStatusFilter(status);
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback((dateRange: [Date | null, Date | null]) => {
    setDateRangeFilter(dateRange);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => setPage(nextPage), []);

  const handleViewDetails = useCallback(async (messageId: string) => {
    if (!messageId) return;

    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError('');
    setDetailsActionError('');
    setDetailsActionSuccess('');
    setDetailsData(null);

    try {
      const response = await emailPipelineService.getRecordDetails(messageId);
      setDetailsData(response);
    } catch (error: unknown) {
      setDetailsError(getErrorMessage(error, 'Failed to load details'));
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleProcessAction = useCallback(async () => {
    if (!detailsRecord?.message_id) return;

    const force = isProcessedStatus(detailsRecord.status);
    setDetailsActionLoading(true);
    setDetailsActionError('');
    setDetailsActionSuccess('');

    try {
      await emailPipelineService.processMessage(detailsRecord.message_id, force);
      setDetailsActionSuccess(force ? 'Force reprocess has been triggered.' : 'Reprocess has been triggered.');

      const [detailsResponse] = await Promise.all([
        emailPipelineService.getRecordDetails(detailsRecord.message_id),
        fetchRecords(),
      ]);
      setDetailsData(detailsResponse);
    } catch (error: unknown) {
      setDetailsActionError(getErrorMessage(error, 'Failed to trigger process action'));
    } finally {
      setDetailsActionLoading(false);
    }
  }, [detailsRecord, fetchRecords]);

  const handleBulkProcessAction = useCallback(async () => {
    const selectedRecords = records.filter((record) => selectedIdSet.has(record.id));
    if (selectedRecords.length === 0) return;

    setBulkActionLoading(true);

    const processResults = await Promise.allSettled(
      selectedRecords.map((record) => {
        if (!record.message_id) {
          return Promise.reject(new Error('Missing message id'));
        }

        return emailPipelineService.processMessage(record.message_id, isProcessedStatus(record.status));
      }),
    );

    const successCount = processResults.filter((result) => result.status === 'fulfilled').length;
    const failedCount = processResults.length - successCount;

    if (successCount > 0) {
      showToast.success(
        `Processed ${successCount} email${successCount > 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}.`,
      );
    } else {
      showToast.error('Failed to process selected emails.');
    }

    setSelectedIds([]);
    await fetchRecords();
    setBulkActionLoading(false);
  }, [fetchRecords, records, selectedIdSet]);

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    setSelectedIds(checked ? records.map((record) => record.id) : []);
  }, [records]);

  const handleToggleRowSelection = useCallback((recordId: string, checked: boolean) => {
    setSelectedIds((previousIds) => {
      if (checked) {
        const nextIds = new Set(previousIds);
        nextIds.add(recordId);
        return Array.from(nextIds);
      }

      return previousIds.filter((id) => id !== recordId);
    });
  }, []);

  const isRecordSelected = useCallback((recordId: string) => selectedIdSet.has(recordId), [selectedIdSet]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  return {
    pageMeta: {
      isSuperAdmin,
      pageTitle: 'Pipeline Status',
    },
    statusCard: {
      model: {
        statusLoading,
        statusError,
        statusData,
      },
    },
    filters: {
      model: {
        stores,
        storesLoading,
        storesError,
        selectedStore,
        showStoreFilter: !lockStoreSelection,
        storeSearchInput,
        statusFilter,
        dateRangeFilter,
        search,
        selectedIdsCount: selectedIds.length,
        bulkActionLoading,
      },
      actions: {
        onSearchChange: handleSearchChange,
        onStoreSearchInputChange: setStoreSearchInput,
        onStoreChange: handleStoreChange,
        onStatusFilterChange: handleStatusFilterChange,
        onDateRangeChange: handleDateRangeChange,
        onBulkProcess: handleBulkProcessAction,
      },
    },
    table: {
      model: {
        records,
        recordsLoading,
        recordsError,
        totalRecords,
        page,
        selectedIdsCount: selectedIds.length,
      },
      actions: {
        onPageChange: handlePageChange,
        isRecordSelected,
        onToggleSelectAll: handleToggleSelectAll,
        onToggleRowSelection: handleToggleRowSelection,
        onViewDetails: handleViewDetails,
        onBulkProcess: handleBulkProcessAction,
      },
    },
    details: {
      model: {
        open: detailsOpen,
        detailsLoading,
        detailsError,
        detailsData,
        detailsActionLoading,
        detailsActionError,
        detailsActionSuccess,
      },
      actions: {
        onClose: () => setDetailsOpen(false),
        onProcessAction: handleProcessAction,
      },
    },
  };
};
